import Address from '@/gf/components/Address'
import AddressModalForm from '@/gf/components/AddressModalForm'
import Card from '@/gf/components/Card'
import EmailAddressModalForm from '@/gf/components/EmailAddressModalForm'
import Tooltip from '@/gf/components/Tooltip'
import Vendor from '@/gf/components/Vendor'
import useMsgs from '@/gf/hooks/useMsgs'
import { TIMELINE_QUERY } from '@/gf/hooks/useTimelineEvents'
import useToggle from '@/gf/hooks/useToggle'
import GraphQL from '@/gf/modules/GraphQL'
import User from '@/gf/modules/User'
import { EDIT_BILLING_ADDRESS, EDIT_SHIPPING_ADDRESS } from '@/gf/mutations/addressess'
import UPDATE_ORDER_EMAIL from '@/gf/mutations/storeOrders'
import { BillingAddress as BillingAddressT, Company, PaymentMethod } from '@/suppliers/_gen/gql'
import { useMutation } from '@apollo/client'
import { Link } from 'react-router-dom'
import AddressMetadata from '../../components/AddressMetadata'
import useSession from '../../hooks/useSession'
import StoreOrderM from '../../modules/StoreOrder'
import { StoreOrder } from './useFetchStoreOrder'
import BillingAddress from '@/gf/components/BillingAddress'

const CustomerCard = ({
  storeOrder,
  company,
  billingAddress,
  spinnerLive,
  spinner,
  reloadStoreOrder,
}: {
  storeOrder: StoreOrder
  company: Pick<Company, 'name'>
  billingAddress: Pick<
    BillingAddressT,
    'lineOne' | 'lineTwo' | 'city' | 'state' | 'postalCode'
  > | null
  spinnerLive: boolean
  spinner: {
    toggle: () => void
    on: () => void
    off: () => void
  }
  reloadStoreOrder: () => Promise<unknown>
}) => {
  const [_, msgr] = useMsgs()
  const [emailAddressModalOpen, emailAddressModalToggle] = useToggle()
  const [shippingAddressModalOpen, shippingAddressModalToggle] = useToggle()
  const [billingAddressModalOpen, billingAddressModalToggle] = useToggle()
  const { user } = useSession()
  const [updateEmail] = useMutation(UPDATE_ORDER_EMAIL)
  const [updateBillingAddress] = useMutation(EDIT_BILLING_ADDRESS)

  const [updateShippingAddress] = useMutation(EDIT_SHIPPING_ADDRESS, {
    refetchQueries: [TIMELINE_QUERY],
  })

  const updateEmailAddress = async (email) => {
    try {
      spinner.on()

      const variables = {
        email,
        storeOrderId: storeOrder.id,
      }

      await updateEmail({ variables })
      await reloadStoreOrder()

      spinner.off()
      emailAddressModalToggle.off()
      const message = 'email address updated!'
      msgr.add(message, 'positive')
    } catch (error) {
      const err = error as { graphQLErrors: { message: string }[] }
      const message = err.graphQLErrors.reduce((acc, { message: msg }) => `${acc}${msg}`, '')
      spinner.off()
      emailAddressModalToggle.off()
      msgr.add(message, 'negative')
    }
  }

  const editShippingAddress = async (newAddress) => {
    try {
      spinner.on()
      const shippingAddress = GraphQL.cleanGraphQlInput(newAddress)

      const variables = {
        storeOrderId: storeOrder.id,
        shippingAddress,
      }

      await updateShippingAddress({ variables })
      await reloadStoreOrder()
      spinner.off()
      shippingAddressModalToggle.off()
      const message = 'Shipping address updated!'
      msgr.add(message, 'positive')
    } catch (error) {
      const err = error as { graphQLErrors: { message: string }[] }
      const message = err.graphQLErrors.reduce((acc, { message: msg }) => `${acc}${msg}`, '')
      spinner.off()
      shippingAddressModalToggle.off()
      msgr.add(message, 'negative')
    }
  }

  const editBillingAddress = async (newAddress) => {
    try {
      spinner.on()
      const billingAddress = GraphQL.cleanGraphQlInput(newAddress)

      const variables = {
        storeOrderId: storeOrder.id,
        billingAddress,
      }

      await updateBillingAddress({ variables })
      await reloadStoreOrder()
      spinner.off()
      billingAddressModalToggle.off()
      const message = 'Billing address updated!'
      msgr.add(message, 'positive')
    } catch (error) {
      const err = error as { graphQLErrors: { message: string }[] }
      const message = err.graphQLErrors.reduce((acc, { message: msg }) => `${acc}${msg}`, '')
      spinner.off()
      billingAddressModalToggle.off()
      msgr.add(message, 'negative')
    }
  }

  const isAdmin = User.isAdmin(user)
  const displayCustomerContactInfo = StoreOrderM.canDisplayCustomerContactInfo(storeOrder, user)

  return (
    <>
      <EmailAddressModalForm
        open={emailAddressModalOpen}
        onClose={emailAddressModalToggle.off}
        email={storeOrder.order.customer.email}
        spinnerLive={spinnerLive}
        onSubmit={updateEmailAddress}
      />
      <AddressModalForm
        title="Edit shipping address"
        open={shippingAddressModalOpen}
        onClose={shippingAddressModalToggle.off}
        initialAddress={storeOrder.shippingAddress}
        onSubmit={editShippingAddress}
      />
      <AddressModalForm
        title="Edit billing address"
        open={billingAddressModalOpen}
        onClose={billingAddressModalToggle.off}
        initialAddress={storeOrder.order.billingAddress}
        onSubmit={editBillingAddress}
      />
      <Card title="Customer">
        {displayCustomerContactInfo && storeOrder.order.customer.user && (
          <Card.Section>
            <Link
              className="text-sm text-blue-500 underline"
              to={`/customers/${StoreOrderM.customerId(storeOrder)}`}
            >
              {storeOrder.order.customer.user?.name}
            </Link>
          </Card.Section>
        )}

        {displayCustomerContactInfo && (
          <Card.Section
            title="Contact Information"
            headerButton={
              isAdmin ? (
                <button
                  className="text-sm text-blue-500 underline cursor-pointer"
                  type="button"
                  onClick={() => emailAddressModalToggle.on()}
                >
                  Edit
                </button>
              ) : (
                <></>
              )
            }
          >
            {!storeOrder.shippingAddress && storeOrder.order.requestForQuote && (
              <p className="text-sm text-gray-700">{storeOrder.order.requestForQuote.fullName}</p>
            )}
            <p className="text-sm text-gray-700">{storeOrder.order.customer.phoneNumber}</p>
            {isAdmin && <p className="text-sm text-gray-700">{storeOrder.order.customer.email}</p>}
          </Card.Section>
        )}

        {storeOrder.shippingAddress && (
          <Card.Section
            title={storeOrder.pickup ? 'Location' : 'Shipping'}
            headerButton={
              isAdmin ? (
                !storeOrder.shippingLocation ||
                storeOrder.paymentMethod === PaymentMethod.Direct ? (
                  <button
                    className="text-sm text-blue-500 underline cursor-pointer"
                    type="button"
                    onClick={() => shippingAddressModalToggle.on()}
                  >
                    Edit
                  </button>
                ) : (
                  <Tooltip text="You cannot edit the shipping location" size="medium" left />
                )
              ) : (
                <></>
              )
            }
          >
            <Address
              hideName={!displayCustomerContactInfo}
              address={storeOrder.shippingAddress}
              className="text-sm text-gray-700"
            />
            {User.isAdmin(user) && storeOrder.shippingLocation?.code && (
              <p className="mt-1 text-sm text-gray-700">
                (Location/Cost Code: {storeOrder.shippingLocation.code})
              </p>
            )}
            {typeof storeOrder.shippingAddress.deliverable === 'boolean' && (
              <AddressMetadata className="mt-2" address={storeOrder.shippingAddress} />
            )}
          </Card.Section>
        )}

        {billingAddress && (
          <Card.Section
            title="Billing"
            headerButton={
              // TODO: update this edit field to edit the "real" address
              isAdmin ? (
                storeOrder.shippingLocation ? (
                  <Tooltip text="You cannot edit the billing location" size="medium" left />
                ) : (
                  <button
                    className="text-sm text-blue-500 underline cursor-pointer"
                    type="button"
                    onClick={() => billingAddressModalToggle.on()}
                  >
                    Edit
                  </button>
                )
              ) : (
                <></>
              )
            }
          >
            <BillingAddress
              className="text-sm text-gray-700"
              companyName={company.name}
              address={billingAddress}
            />
          </Card.Section>
        )}

        {storeOrder.paymentMethod === PaymentMethod.Direct && storeOrder.vendor && (
          <Card.Section title="Direct Pay">
            <Vendor className="text-sm text-gray-700" vendor={storeOrder.vendor} />
          </Card.Section>
        )}
      </Card>
    </>
  )
}

export default CustomerCard
